import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ModalComponent } from '../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../shared/helpers/http-error';
import { filterBusinessRoles } from '../../shared/helpers/role.helpers';
import { fullName, initials } from '../../shared/helpers/user.helpers';
import { AuthService } from '../../shared/services/auth.service';
import { MeService } from '../../shared/services/me.service';
import { TPipe } from '../../shared/pipe/t.pipe';

@Component({
  selector: 'app-profile',
  imports: [TPipe, 
    CommonModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    ModalComponent,
  ],
  templateUrl: './profile.component.html',
})
export class ProfileComponent {
  private readonly auth = inject(AuthService);
  private readonly meService = inject(MeService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  /** Read-only mirror of the auth state — auto-updates when /me refreshes. */
  readonly user = this.auth.user;
  readonly displayName = computed(() => fullName(this.user()));
  readonly userInitials = computed(() => initials(this.user()));
  /** Filtered + alpha-sorted business roles (no `default-*` / lowercase noise).
   *  `filterBusinessRoles` returns plain strings → use `localeCompare` directly
   *  (`byBusinessThenAlpha` is for `RoleDTO` objects with a `.name` field). */
  readonly businessRoles = computed(() =>
    filterBusinessRoles(this.user()?.roles ?? []).sort((a, b) => a.localeCompare(b)),
  );
  readonly permissions = computed(() =>
    [...(this.user()?.permissions ?? [])].sort(),
  );

  // ── Personal info modal ───────────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly editSaving = signal(false);
  readonly editError = signal<string | null>(null);
  readonly editForm = this.fb.group({
    firstName: ['', [Validators.maxLength(80)]],
    lastName: ['', [Validators.maxLength(80)]],
  });

  // ── Password modal ────────────────────────────────────────────────────
  readonly passwordOpen = signal(false);
  readonly passwordSaving = signal(false);
  readonly passwordError = signal<string | null>(null);
  readonly passwordOk = signal(false);
  readonly passwordForm = this.fb.group(
    {
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      newPasswordRepeat: ['', [Validators.required]],
    },
    { validators: ProfileComponent.matchPasswordsValidator },
  );

  // ── Email change (2-step OTP) ─────────────────────────────────────────
  readonly emailOpen = signal(false);
  readonly emailStep = signal<'request' | 'verify'>('request');
  readonly emailSaving = signal(false);
  readonly emailError = signal<string | null>(null);
  readonly emailForm = this.fb.group({
    email: ['', [Validators.required, Validators.email, Validators.maxLength(50)]],
    otp: ['', [Validators.pattern(/^\d{6}$/)]],
  });

  // ── Phone change (2-step OTP) ─────────────────────────────────────────
  readonly phoneOpen = signal(false);
  readonly phoneStep = signal<'request' | 'verify'>('request');
  readonly phoneSaving = signal(false);
  readonly phoneError = signal<string | null>(null);
  readonly phoneForm = this.fb.group({
    phone: ['', [Validators.required, Validators.maxLength(20)]],
    otp: ['', [Validators.pattern(/^\d{6}$/)]],
  });

  // ────────────────────────────────────────────────────────────────────────
  // Personal info
  // ────────────────────────────────────────────────────────────────────────
  openEdit(): void {
    const u = this.user();
    this.editForm.reset({
      firstName: u?.firstName ?? '',
      lastName: u?.lastName ?? '',
    });
    this.editError.set(null);
    this.editOpen.set(true);
  }
  closeEdit(): void {
    if (this.editSaving()) return;
    this.editOpen.set(false);
  }
  submitEdit(): void {
    if (this.editForm.invalid || this.editSaving()) {
      this.editForm.markAllAsTouched();
      return;
    }
    const raw = this.editForm.getRawValue();
    this.editSaving.set(true);
    this.editError.set(null);
    this.meService
      .updateProfile({
        firstName: raw.firstName.trim() || null,
        lastName: raw.lastName.trim() || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.refreshAndCloseEdit(),
        error: (err: unknown) => {
          this.editSaving.set(false);
          this.editError.set(toErrorMessage(err, 'errors.updateFailed'));
        },
      });
  }
  private refreshAndCloseEdit(): void {
    // Resync auth.user signal from /me so the header avatar/name update too.
    this.auth.me().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      complete: () => {
        this.editSaving.set(false);
        this.editOpen.set(false);
      },
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Password
  // ────────────────────────────────────────────────────────────────────────
  openPassword(): void {
    this.passwordForm.reset({
      oldPassword: '',
      newPassword: '',
      newPasswordRepeat: '',
    });
    this.passwordError.set(null);
    this.passwordOk.set(false);
    this.passwordOpen.set(true);
  }
  closePassword(): void {
    if (this.passwordSaving()) return;
    this.passwordOpen.set(false);
  }
  submitPassword(): void {
    if (this.passwordForm.invalid || this.passwordSaving()) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    const raw = this.passwordForm.getRawValue();
    this.passwordSaving.set(true);
    this.passwordError.set(null);
    this.meService
      .changePassword({
        oldPassword: raw.oldPassword,
        newPassword: raw.newPassword,
        newPasswordRepeat: raw.newPasswordRepeat,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.passwordSaving.set(false);
          this.passwordOk.set(true);
          // Refresh /me in case `changePassword` flag flipped.
          this.auth.me().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        },
        error: (err: unknown) => {
          this.passwordSaving.set(false);
          this.passwordError.set(
            toErrorMessage(err, 'errors.passwordChangeFailed', {
              400: 'errors.profile.badOldPassword',
            }),
          );
        },
      });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Email (OTP)
  // ────────────────────────────────────────────────────────────────────────
  openEmail(): void {
    this.emailStep.set('request');
    this.emailForm.reset({ email: '', otp: '' });
    this.emailError.set(null);
    this.emailOpen.set(true);
  }
  closeEmail(): void {
    if (this.emailSaving()) return;
    this.emailOpen.set(false);
  }
  submitEmailRequest(): void {
    const c = this.emailForm.controls.email;
    if (c.invalid || this.emailSaving()) {
      c.markAsTouched();
      return;
    }
    this.emailSaving.set(true);
    this.emailError.set(null);
    this.meService
      .requestEmailOtp({ email: c.value.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.emailSaving.set(false);
          this.emailStep.set('verify');
        },
        error: (err: unknown) => {
          this.emailSaving.set(false);
          this.emailError.set(
            toErrorMessage(err, 'errors.otpSendFailed'),
          );
        },
      });
  }
  submitEmailVerify(): void {
    if (this.emailForm.invalid || this.emailSaving()) {
      this.emailForm.markAllAsTouched();
      return;
    }
    const raw = this.emailForm.getRawValue();
    this.emailSaving.set(true);
    this.emailError.set(null);
    this.meService
      .updateEmail({ email: raw.email.trim(), otp: raw.otp })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.refreshAndCloseEmail(),
        error: (err: unknown) => {
          this.emailSaving.set(false);
          this.emailError.set(
            toErrorMessage(err, 'errors.codeVerifyFailed', {
              400: 'errors.profile.badOtp',
            }),
          );
        },
      });
  }
  private refreshAndCloseEmail(): void {
    this.auth.me().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      complete: () => {
        this.emailSaving.set(false);
        this.emailOpen.set(false);
      },
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Phone (OTP)
  // ────────────────────────────────────────────────────────────────────────
  openPhone(): void {
    this.phoneStep.set('request');
    this.phoneForm.reset({ phone: '', otp: '' });
    this.phoneError.set(null);
    this.phoneOpen.set(true);
  }
  closePhone(): void {
    if (this.phoneSaving()) return;
    this.phoneOpen.set(false);
  }
  submitPhoneRequest(): void {
    const c = this.phoneForm.controls.phone;
    if (c.invalid || this.phoneSaving()) {
      c.markAsTouched();
      return;
    }
    this.phoneSaving.set(true);
    this.phoneError.set(null);
    this.meService
      .requestPhoneOtp({ phone: c.value.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.phoneSaving.set(false);
          this.phoneStep.set('verify');
        },
        error: (err: unknown) => {
          this.phoneSaving.set(false);
          this.phoneError.set(
            toErrorMessage(err, 'errors.otpSendFailed'),
          );
        },
      });
  }
  submitPhoneVerify(): void {
    if (this.phoneForm.invalid || this.phoneSaving()) {
      this.phoneForm.markAllAsTouched();
      return;
    }
    const raw = this.phoneForm.getRawValue();
    this.phoneSaving.set(true);
    this.phoneError.set(null);
    this.meService
      .updatePhone({ phone: raw.phone.trim(), otp: raw.otp })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.refreshAndClosePhone(),
        error: (err: unknown) => {
          this.phoneSaving.set(false);
          this.phoneError.set(
            toErrorMessage(err, 'errors.codeVerifyFailed', {
              400: 'errors.profile.badOtp',
            }),
          );
        },
      });
  }
  private refreshAndClosePhone(): void {
    this.auth.me().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      complete: () => {
        this.phoneSaving.set(false);
        this.phoneOpen.set(false);
      },
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────────────────
  /**
   * Cross-field validator : `newPassword === newPasswordRepeat`. Marks the
   * repeat control as `passwordMismatch: true` instead of erroring on the
   * group, so the inline error is on the repeat field.
   */
  private static matchPasswordsValidator(group: any): null {
    const n = group.get('newPassword')?.value;
    const r = group.get('newPasswordRepeat')?.value;
    const repeat = group.get('newPasswordRepeat');
    if (!repeat) return null;
    if (n && r && n !== r) {
      repeat.setErrors({ ...(repeat.errors ?? {}), passwordMismatch: true });
    } else if (repeat.errors?.['passwordMismatch']) {
      const { passwordMismatch, ...rest } = repeat.errors;
      repeat.setErrors(Object.keys(rest).length ? rest : null);
    }
    return null;
  }

}
